import { createContext, useContext } from 'react'
import type { ReportingScopeMember } from '@/types/shared'

/**
 * ONE COLOUR PER HOTEL, THE SAME COLOUR ON EVERY PAGE.
 *
 * A group's lists mix hotels, and every hotel badge used to be the same teal — which told the
 * operator "this row belongs to some hotel" and nothing more. Scanning a mixed list for "which of
 * these are Wembar's" meant reading every badge. A colour does that job at a glance; the name is
 * then confirmation rather than the only signal.
 *
 * WHY AN INDEX AND NOT A HASH: a hash of the property id needs no plumbing, but collides. Five
 * hotels over a ten-colour palette collide about 70% of the time, and two hotels sharing a colour
 * is precisely the thing this exists to prevent. The index into `reportingScope.members` cannot
 * collide below palette length, and that array is ordered by the backend (lead first, then
 * alphabetical — PropertyGroup#members) identically on every page of the group, so a hotel keeps
 * its colour as the operator moves between Rooms Setup, the calendar, overrides and the change log.
 *
 * WHY THESE HUES: the tables these badges sit in already spend emerald on an increase, rose on a
 * decrease and amber on "needs you". An identity colour that borrows one of those reads as a
 * status. What is left is the blue-violet-magenta arc plus neutral slate, which is enough
 * separation for the two-to-six hotels a real group holds.
 *
 * The lead is always `primary`. It is the hotel whose settings price every row, it wears the crown,
 * and pinning it means the one colour an operator learns first never moves between groups.
 */
export interface HotelTone {
  /** Badge fill + text. */
  chip: string
  /** Section headings and other text-only treatments of the same hotel. */
  text: string
}

export const LEAD_TONE: HotelTone = {
  chip: 'bg-primary/10 text-primary',
  text: 'text-primary',
}

/** Members, in `reportingScope.members` order after the lead. Cycles if a group outgrows it. */
export const MEMBER_TONES: HotelTone[] = [
  { chip: 'bg-indigo-100 text-indigo-700', text: 'text-indigo-700' },
  { chip: 'bg-fuchsia-100 text-fuchsia-700', text: 'text-fuchsia-700' },
  { chip: 'bg-sky-100 text-sky-700', text: 'text-sky-700' },
  { chip: 'bg-violet-100 text-violet-700', text: 'text-violet-700' },
  { chip: 'bg-cyan-100 text-cyan-700', text: 'text-cyan-700' },
  { chip: 'bg-slate-200 text-slate-700', text: 'text-slate-700' },
]

/**
 * The group's hotels, or `null` when there is no group (or when a component renders outside the
 * operator layout, as in a unit test). `null` means "fall back to the old two-tone treatment" —
 * which is what keeps an ungrouped property's surfaces byte-identical to how they looked before
 * groups existed.
 */
export const HotelPaletteContext = createContext<ReportingScopeMember[] | null>(null)

/**
 * Resolve a hotel to its tone. Prefer `propertyId` — it is the identity. `name` is the fallback for
 * the handful of surfaces that carry a hotel's name but not its id; within one group the names are
 * distinct in practice, and the worst case of a clash is two hotels sharing a colour, never a wrong
 * colour on a right row.
 *
 * Returns `null` when there is no palette or the hotel is not in it, so callers keep their previous
 * appearance rather than guessing.
 */
export function resolveHotelTone(
  members: ReportingScopeMember[] | null,
  propertyId?: number | null,
  name?: string | null,
): HotelTone | null {
  if (!members || members.length === 0) return null

  const index = members.findIndex((m) =>
    propertyId != null ? m.id === propertyId : name != null && m.name === name,
  )
  if (index < 0) return null
  if (members[index].isLead) return LEAD_TONE

  // The lead occupies members[0] in the backend's ordering, so members start at index 1. Subtracting
  // the number of leads before this row keeps the mapping stable even if that ordering ever changes.
  const leadsBefore = members.slice(0, index).filter((m) => m.isLead).length
  return MEMBER_TONES[(index - leadsBefore) % MEMBER_TONES.length]
}

/** Hook form for components inside the operator layout. */
export function useHotelTone(): (propertyId?: number | null, name?: string | null) => HotelTone | null {
  const members = useContext(HotelPaletteContext)
  return (propertyId, name) => resolveHotelTone(members, propertyId, name)
}
